import {
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  } from "firebase/firestore";
import { createLeagueBatch, leagueCollection, leagueCollections, leagueDocument } from "./leagueRepository";

function requiredSeasonId(seasonId) {
  const value = String(seasonId || "").trim();
  if (!value) throw new Error("Matrix League requires a seasonId.");
  return value;
}

export function fixturesRef(orgId) {
  return leagueCollection(orgId, leagueCollections.fixtures);
}

export function fixtureRef(orgId, fixtureId) {
  return leagueDocument(orgId, leagueCollections.fixtures, fixtureId);
}

export function subscribeFixtures(orgId, seasonId, onValue, onError) {
  const fixturesQuery = query(fixturesRef(orgId), orderBy("round", "asc"));
  return onSnapshot(fixturesQuery, (snapshot) => {
    const fixtures = snapshot.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((fixture) => !seasonId || fixture.seasonId === seasonId)
      .sort((a, b) => Number(a.round || 0) - Number(b.round || 0) || Number(a.matchIndex || 0) - Number(b.matchIndex || 0));
    onValue(fixtures);
  }, onError);
}

function rotate(items) {
  if (items.length <= 2) return items;
  return [items[0], items[items.length - 1], ...items.slice(1, -1)];
}

function roundDate(startDate, round, daysBetweenRounds) {
  if (!startDate) return null;
  const date = new Date(`${startDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + ((round - 1) * daysBetweenRounds));
  return date.toISOString();
}

export function buildRoundRobinFixtures(clubs = [], { doubleRound = true, startDate = "", daysBetweenRounds = 7 } = {}) {
  const active = clubs.filter((club) => club?.active !== false).map((club) => ({ id: club.id, name: club.name }));
  if (active.length < 2) return [];
  const teams = active.length % 2 === 0 ? active : [...active, null];
  const rounds = [];
  let rotation = [...teams];
  const firstLegRounds = rotation.length - 1;

  for (let round = 1; round <= firstLegRounds; round += 1) {
    for (let index = 0; index < rotation.length / 2; index += 1) {
      const left = rotation[index];
      const right = rotation[rotation.length - 1 - index];
      if (!left || !right) continue;
      const swap = round % 2 === 0;
      const home = swap ? right : left;
      const away = swap ? left : right;
      rounds.push({
        round,
        matchIndex: index,
        scheduledAt: roundDate(startDate, round, daysBetweenRounds),
        homeClubId: home.id,
        homeClubName: home.name,
        awayClubId: away.id,
        awayClubName: away.name,
        status: "scheduled",
        homeScore: null,
        awayScore: null,
        homeNeoScore: null,
        homeMorpheusScore: null,
        awayNeoScore: null,
        awayMorpheusScore: null,
        homeOperationalScore: 0,
        awayOperationalScore: 0,
        homeOperationalBonus: 0,
        awayOperationalBonus: 0,
        locked: false,
      });
    }
    rotation = rotate(rotation);
  }

  if (!doubleRound) return rounds;
  return [
    ...rounds,
    ...rounds.map((fixture) => {
      const round = fixture.round + firstLegRounds;
      return {
        ...fixture,
        round,
        scheduledAt: roundDate(startDate, round, daysBetweenRounds),
        homeClubId: fixture.awayClubId,
        homeClubName: fixture.awayClubName,
        awayClubId: fixture.homeClubId,
        awayClubName: fixture.homeClubName,
      };
    }),
  ];
}

export async function generateFixtures(orgId, season, clubs, options = {}) {
  const seasonId = requiredSeasonId(season?.id);
  const fixtures = buildRoundRobinFixtures(clubs, {
    doubleRound: options.doubleRound !== false,
    startDate: season?.startDate || options.startDate || "",
    daysBetweenRounds: Number(season?.daysBetweenRounds || options.daysBetweenRounds || 7),
  });
  if (!fixtures.length) throw new Error("Add at least two active clubs before generating fixtures.");

  const existing = await getDocs(fixturesRef(orgId));
  const existingForSeason = existing.docs.filter((item) => item.data()?.seasonId === seasonId);
  const hasCompleted = existingForSeason.some((item) => item.data()?.status === "complete");
  if (hasCompleted && !options.force) {
    throw new Error("This season already has completed results. Regeneration is blocked to protect the table.");
  }

  const batch = createLeagueBatch();
  existingForSeason.forEach((item) => batch.delete(item.ref));
  fixtures.forEach((fixture) => {
    const id = `${seasonId}-r${String(fixture.round).padStart(2, "0")}-m${String(fixture.matchIndex + 1).padStart(2, "0")}`;
    batch.set(fixtureRef(orgId, id), {
      ...fixture,
      seasonId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return { count: fixtures.length, totalRounds: Math.max(...fixtures.map((fixture) => fixture.round)) };
}

function validScore(value, label) {
  const number = Number(value);
  if (!Number.isInteger(number) || number < 0) throw new Error(`${label} must be a whole number of zero or more.`);
  return number;
}

export async function saveFixtureResult(orgId, fixture, result) {
  if (fixture?.locked) throw new Error("This fixture is locked and can no longer be edited.");
  const homeScore = validScore(result?.homeScore, "Home score");
  const awayScore = validScore(result?.awayScore, "Away score");
  return updateDoc(fixtureRef(orgId, fixture.id), {
    homeScore,
    awayScore,
    homeNeoScore: Number(result?.homeNeoScore || 0),
    homeMorpheusScore: Number(result?.homeMorpheusScore || 0),
    awayNeoScore: Number(result?.awayNeoScore || 0),
    awayMorpheusScore: Number(result?.awayMorpheusScore || 0),
    homeOperationalScore: Number(result?.homeOperationalScore || 0),
    awayOperationalScore: Number(result?.awayOperationalScore || 0),
    homeOperationalBonus: Number(result?.homeOperationalBonus || 0),
    awayOperationalBonus: Number(result?.awayOperationalBonus || 0),
    status: "complete",
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateFixtureStatus(orgId, fixtureId, status, extra = {}) {
  return setDoc(fixtureRef(orgId, fixtureId), {
    status,
    ...extra,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function setRoundStatus(orgId, fixtures, round, status) {
  const roundFixtures = fixtures.filter((fixture) => Number(fixture.round) === Number(round));
  if (!roundFixtures.length) throw new Error(`Round ${round} has no fixtures.`);
  const batch = createLeagueBatch();
  roundFixtures.forEach((fixture) => {
    batch.set(fixtureRef(orgId, fixture.id), {
      status: fixture.status === "complete" && status === "live" ? "complete" : status,
      startedAt: status === "live" ? serverTimestamp() : fixture.startedAt || null,
      closedAt: status === "closed" ? serverTimestamp() : null,
      locked: status === "closed",
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
  await batch.commit();
}

export async function deleteFixture(orgId, fixtureId) {
  return deleteDoc(fixtureRef(orgId, fixtureId));
}